import { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchablePicker, { productMatch, renderProductRow } from "@/components/app/SearchablePicker";
import { ArrowUpDown } from "lucide-react";

const SORTS = [
  { key: "latest", label: "Latest Price (Low → High)", cmp: (a, b) => num(a.latest_price) - num(b.latest_price) },
  { key: "latest-desc", label: "Latest Price (High → Low)", cmp: (a, b) => num(b.latest_price) - num(a.latest_price) },
  { key: "lowest", label: "Lowest Ever (Low → High)", cmp: (a, b) => num(a.lowest_price) - num(b.lowest_price) },
  { key: "delivery", label: "Delivery Time (Fast → Slow)", cmp: (a, b) => (a.last_delivery_days ?? 9999) - (b.last_delivery_days ?? 9999) },
  { key: "vendor", label: "Vendor A → Z", cmp: (a, b) => (a._vendor || "").localeCompare(b._vendor || "") },
  { key: "brand", label: "Brand A → Z", cmp: (a, b) => (a._brand || "").localeCompare(b._brand || "") },
];
const num = (v) => (v == null ? Number.POSITIVE_INFINITY : Number(v));

export default function ProductComparison() {
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [productId, setProductId] = useState("");
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState("latest");

  useEffect(() => {
    Promise.all([
      api.get("/purchasing/products"),
      api.get("/purchasing/brands"),
      api.get("/purchasing/vendors"),
    ]).then(([p, b, v]) => { setProducts(p.data); setBrands(b.data); setVendors(v.data); });
  }, []);

  useEffect(() => {
    if (!productId) { setRows([]); return; }
    api.get(`/purchasing/product-comparison?product_id=${productId}`).then((r) => setRows(r.data));
  }, [productId]);

  const brandName = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);
  const vendorName = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.name])), [vendors]);

  // Sort key needs vendor/brand names on each row.
  const enriched = rows.map((r) => ({ ...r, _vendor: vendorName[r.vendor_id] || "", _brand: brandName[r.brand_id] || "" }));
  const sortFn = SORTS.find((s) => s.key === sort)?.cmp || ((a, b) => 0);
  const sorted = [...enriched].sort(sortFn);

  return (
    <div data-testid="pv-comparison-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Product Comparison"
        subtitle="For a picked product, compare every vendor × brand — latest, last purchased, lowest, highest."
      />
      <div className="p-4 sm:p-8">
        <div className="flex flex-wrap gap-3 mb-6 items-end">
          <div>
            <label className="label-eyebrow block mb-1">Product</label>
            <div className="w-64">
              <SearchablePicker
                items={products}
                value={productId}
                onChange={setProductId}
                placeholder="Pick a product"
                testid="pv-cmp-product"
                selectedLabel={(p) => p.name}
                matchItem={productMatch}
                renderRow={renderProductRow}
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1 flex items-center gap-1"><ArrowUpDown className="w-3 h-3" /> Sort</label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="rounded-none h-10 w-64" data-testid="pv-cmp-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{SORTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Vendor", "Brand", "Latest", "Last Purchased", "Lowest", "Highest", "Delivery", "Quotes", "Purchases", "Last Updated"].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={`${r.vendor_id}|${r.brand_id || ""}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-cmp-row-${r.vendor_id}-${r.brand_id || "none"}`}>
                  <td className="px-4 py-3 text-xs font-semibold">{r._vendor || "—"}</td>
                  <td className="px-4 py-3 text-xs">{r._brand || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{r.latest_price != null ? inr(r.latest_price) : "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{r.last_purchase_price != null ? inr(r.last_purchase_price) : "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-[#10B981]">{r.lowest_price != null ? inr(r.lowest_price) : "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-[#DC2626]">{r.highest_price != null ? inr(r.highest_price) : "—"}</td>
                  <td className="px-4 py-3 text-xs">{r.last_delivery_days != null ? `${r.last_delivery_days} d` : "—"}</td>
                  <td className="px-4 py-3 text-xs">{r.quotes || 0}</td>
                  <td className="px-4 py-3 text-xs">{r.purchases || 0}</td>
                  <td className="px-4 py-3 text-xs font-mono">{r.latest_date || "—"}</td>
                </tr>
              ))}
              {sorted.length === 0 && <tr><td colSpan={10} className="text-center text-sm text-[#9CA3AF] py-12">
                {productId ? "No pricing data for this product yet. Record a quotation or PO to populate." : "Pick a product to compare vendors and brands."}
              </td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
